import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  useTheme,
} from '@mui/material'
import { ExpandMoreOutlined } from '@mui/icons-material'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import CancelIcon from '@mui/icons-material/Cancel'
import { useMutatequestionadd, useUpdateQuestion } from './HrQuestionhook'
import { BackButton } from '../../components/navigation/BackButton'
import { useLocation, useNavigate } from 'react-router-dom'
import SnackBarContext from '../../context/SnackBarContext'

interface Question {
  questionText: string
  questionType: string
  options: string[]
  correctAnswer: string
  isRequired: boolean
  isVisible: boolean
  errorMessage: string
  isLunchBreak?: boolean
}

const initialState = {
  questionText: '',
  questionType: '',
  options: [''],
  correctAnswer: '',
  isRequired: false,
  isVisible: false,
  errorMessage: '',
}

const editInitialState = {
  questionText: '',
  questionType: '',
  options: [''],
  correctAnswer: '',
  isRequired: false,
  isVisible: false,
  errorMessage: '',
}

function AddQuestionForm() {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const questionId = location.state?.questionId
  const { mutateAsync: questionAsync } = useMutatequestionadd('questions')
  const { mutateAsync: updateQuestionAsync } = useUpdateQuestion()
  const [error, setError] = useState<string | null>('')
  const mode = location.state?.mode || 'add'
  const [questions, setQuestions] = useState<Question[]>([
    mode === 'edit' ? editInitialState : initialState,
  ])
  const { toggleSnackbar } = useContext(SnackBarContext)
  useEffect(() => {
    if (mode === 'edit') {
      const questionData = location.state?.data || editInitialState
      // Decode Yes/No-Lunch questions
      const decodedQuestionData = {
        ...questionData,
        questionType: questionData.isLunchBreak
          ? 'Yes/No-Lunch'
          : questionData.questionType,
      }
      setQuestions([decodedQuestionData])
    }
  }, [mode, location.state?.data])

  const typography = {
    fontSize: '14px',
    color: '#5A525B',
    paddingBottom: 0.5,
    fontWeight: 'bold',
  }

  const btnStyle = {
    py: 1.8,
    marginTop: '15px',
    color: '#FFF',
    background: '#F0845D',
    fontSize: '13px',
    textTransform: 'none',
    boxShadow: 'none',
    borderRadius: 1.8,
  }

  const inputStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #DBDBDB',
    padding: theme.spacing(0.8, 1.6, 0.9, 1.6),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  }

  const handleQuestionTypeChange = (index: number, value: string) => {
    const updatedQuestions = questions.map((q, idx) => {
      if (idx === index) {
        return {
          ...q,
          questionType: value,
          options:
            value === 'multipleChoice'
              ? ['']
              : value === 'shortAnswer'
              ? []
              : q.options,
          correctAnswer:
            value === 'multipleChoice' || value === 'shortAnswer'
              ? ''
              : q.correctAnswer,
          isLunchBreak: value === 'Yes/No-Lunch' ? true : false,
        }
      }
      return q
    })
    setQuestions(updatedQuestions)
  }

  const handleAddOption = (index: number) => {
    if (questions[index].options.length < 4) {
      const updatedQuestions = questions.map((q, idx) =>
        idx === index ? { ...q, options: [...q.options, ''] } : q
      )
      setQuestions(updatedQuestions)
    } else {
      console.log('Maximum options limit reached.')
    }
  }

  const handleOptionChange = (
    questionIndex: number,
    optionIndex: number,
    value: string
  ) => {
    const trimmedValue = value.trimStart()
    const updatedQuestions = questions.map((q, idx) => {
      if (idx === questionIndex) {
        const newOptions = q.options.map((opt, optIndex) =>
          optIndex === optionIndex ? trimmedValue : opt
        )
        return { ...q, options: newOptions }
      }
      return q
    })
    setQuestions(updatedQuestions)
  }

  const handleCorrectAnswerChange = (questionIndex: number, option: string) => {
    const updatedQuestions = questions.map((question, index) => {
      if (index === questionIndex) {
        return {
          ...question,
          correctAnswer: option,
        }
      }
      return question
    })
    setQuestions(updatedQuestions)
  }

  const handleshortAnswerChange = (questionIndex: number, value: string) => {
    const updatedQuestions = questions.map((q, idx) => {
      if (idx === questionIndex) {
        return { ...q, correctAnswer: value }
      }
      return q
    })
    setQuestions(updatedQuestions)
  }

  const handleCancelOption = (questionIndex: number, optionIndex: number) => {
    const updatedQuestions = questions.map((q, idx) => {
      if (idx === questionIndex) {
        const newOptions = q.options.filter(
          (_, optIdx: number) => optIdx !== optionIndex
        )
        let newCorrectAnswer = q.correctAnswer
        if (q.correctAnswer === q.options[optionIndex]) {
          newCorrectAnswer = ''
        }
        return { ...q, options: newOptions, correctAnswer: newCorrectAnswer }
      }
      return q
    })
    setQuestions(updatedQuestions)
  }

  const handleYesNoAnswer = (questionIndex: number, answer: boolean) => {
    const updatedQuestions = questions.map((q, idx) => {
      if (idx === questionIndex) {
        return { ...q, correctAnswer: answer.toString(), errorMessage: '' }
      }
      return q
    })
    setQuestions(updatedQuestions)
  }

  const handlePropertyChange = (
    index: number,
    property: string,
    value: boolean
  ) => {
    const updatedQuestions = questions.map((q, idx) => {
      if (idx === index) {
        return {
          ...q,
          [property]: value,
        }
      }
      return q
    })
    setQuestions(updatedQuestions)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const isValid = questions.every(
      q => q.questionText.trim() !== '' && q.questionType.trim() !== ''
    )

    if (isValid) {
      let hasError = false

      const updatedQuestions = questions.map(q => {
        let errorMessage = ''
        if (q.questionType === 'multipleChoice') {
          if (q.options.length === 0 || q.correctAnswer === '') {
            errorMessage = 'Please select an answer.'
            hasError = true
          }
        } else if (
          q.questionType === 'Yes/No' ||
          q.questionType === 'Yes/No-Lunch'
        ) {
          if (q.correctAnswer !== 'true' && q.correctAnswer !== 'false') {
            errorMessage = 'Please select Yes or No.'
            hasError = true
          }
        } else if (q.questionType === 'shortAnswer') {
          if (q.correctAnswer.trim() === '') {
            errorMessage = 'Please enter a valid answer.'
            hasError = true
          }
        }
        return {
          ...q,
          errorMessage: errorMessage,
        }
      })

      setQuestions(updatedQuestions)
      if (hasError) {
        setError('')
        return
      }
      try {
        await questionAsync({ questions })
        toggleSnackbar('Questions submitted successfully', 'success')
        navigate(-1)
        setQuestions([initialState])
      } catch (error) {
        setError('Failed to submit questions. Please try again.')
        console.error('Error submitting questions:', error)
      }
    } else {
      setError('Please fill all required fields.')
    }
  }
  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const isValid = questions.every(
      q => q.questionText.trim() !== '' && q.questionType.trim() !== ''
    )
    if (isValid) {
      let hasError = false
      const updatedQuestions = questions.map(q => {
        let errorMessage = ''
        if (q.questionType === 'multipleChoice') {
          if (q.options.length === 0 || q.correctAnswer === '') {
            errorMessage = 'Please select an answer.'
            hasError = true
          }
        } else if (
          q.questionType === 'Yes/No' ||
          q.questionType === 'Yes/No-Lunch'
        ) {
          if (q.correctAnswer !== 'true' && q.correctAnswer !== 'false') {
            errorMessage = 'Please select Yes or No.'
            hasError = true
          }
        } else if (q.questionType === 'shortAnswer') {
          if (q.correctAnswer.trim() === '') {
            errorMessage = 'Please enter a valid answer.'
            hasError = true
          }
        }
        return {
          ...q,
          errorMessage: errorMessage,
        }
      })

      setQuestions(updatedQuestions)

      if (hasError) {
        setError('')
        return
      }
      try {
        await updateQuestionAsync({ id: questionId, data: { questions } })
        toggleSnackbar('Questions updated successfully', 'success')
        navigate(-1)
        setError('')
      } catch (error) {
        setError('Failed to update questions. Please try again.')
        console.error('Error updating questions:', error)
      }
    } else {
      setError('Please fill all required fields.')
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <BackButton />
      <Typography variant='h6' sx={{ mb: 2 }}>
        {mode === 'edit' ? 'Edit Question' : 'Add New Question'}
      </Typography>
      {error && (
        <Typography color='error' sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
      <form onSubmit={mode === 'edit' ? handleUpdate : handleSubmit}>
        {questions.map((question, qIndex) => (
          <Card key={qIndex} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ marginBottom: '15px' }}>
              <Typography sx={typography}>Question Text</Typography>
              <FormControl
                fullWidth
                required
                error={question.questionText.trim() === ''}>
                <Input
                  disableUnderline
                  placeholder='Enter question text'
                  value={question.questionText}
                  onChange={e => {
                    const updatedQuestions = questions.map((q, idx) =>
                      idx === qIndex
                        ? { ...q, questionText: e.target.value.trimStart() }
                        : q
                    )
                    setQuestions(updatedQuestions)
                  }}
                  sx={inputStyle}
                />
              </FormControl>
            </Box>
            <Box
              sx={{
                marginTop: '15px',
                display: 'flex',
                flexDirection: 'column',
              }}>
              <Typography sx={typography}>Select the question type</Typography>
              <Box sx={inputStyle}>
                <FormControl
                  fullWidth
                  required
                  error={question.questionType.trim() === ''}>
                  <Select
                    id='demo-simple-select'
                    IconComponent={ExpandMoreOutlined}
                    sx={{
                      width: '100%',
                      border: 'none',
                      boxShadow: 'none',
                      '& .MuiSelect-select': {
                        padding: theme.spacing(0.8, 0, 0.8, 0),
                      },
                      '&:hover, & fieldset, & fieldset:hover': {
                        border: 'none',
                        boxShadow: 'none',
                      },
                    }}
                    value={question.questionType || ''}
                    onChange={e =>
                      handleQuestionTypeChange(qIndex, e.target.value)
                    }
                    fullWidth
                    displayEmpty
                    renderValue={selected => {
                      if (selected === '') {
                        return (
                          <span style={{ color: 'grey', opacity: '0.6' }}>
                            {'Select the question type'}
                          </span>
                        )
                      }
                      return selected
                    }}>
                    <MenuItem value='multipleChoice'>Multiple Choice</MenuItem>
                    <MenuItem value='Yes/No'>Yes/No</MenuItem>
                    <MenuItem value='shortAnswer'>Short Answer</MenuItem>
                    <MenuItem value='Yes/No-Lunch'>Yes/No-Lunch</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            {question.questionType === 'multipleChoice' && (
              <>
                {question.options.map((option, oIndex) => (
                  <Box
                    key={oIndex}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      mt: 2,
                    }}>
                    <FormControl
                      fullWidth
                      required
                      error={option.trim() === ''}>
                      <Input
                        disableUnderline
                        placeholder={`Option ${oIndex + 1}`}
                        value={option}
                        onChange={e =>
                          handleOptionChange(qIndex, oIndex, e.target.value)
                        }
                        sx={{ ...inputStyle, flex: 1 }}
                      />
                    </FormControl>
                    <Button
                      sx={{ ml: 2 }}
                      variant='outlined'
                      color='error'
                      onClick={() => handleCancelOption(qIndex, oIndex)}>
                      <CancelIcon />
                    </Button>
                  </Box>
                ))}
                <Button
                  variant='contained'
                  sx={{ ...btnStyle, width: '20%', mb: 3 }}
                  onClick={() => handleAddOption(qIndex)}
                  disabled={question.options.length >= 4}>
                  Add Option
                </Button>
                <Typography sx={typography}>Select Correct Answer</Typography>
                <Box sx={inputStyle}>
                  <FormControl
                    fullWidth
                    required
                    error={question.questionType.trim() === ''}>
                    <Select
                      id='demo-simple-select'
                      IconComponent={ExpandMoreOutlined}
                      sx={{
                        width: '100%',
                        border: 'none',
                        boxShadow: 'none',
                        '& .MuiSelect-select': {
                          padding: theme.spacing(0.8, 0, 0.8, 0),
                        },
                        '&:hover, & fieldset, & fieldset:hover': {
                          border: 'none',
                          boxShadow: 'none',
                        },
                      }}
                      value={question.correctAnswer || ''}
                      onChange={e =>
                        handleCorrectAnswerChange(qIndex, e.target.value)
                      }
                      displayEmpty
                      renderValue={selected => {
                        if (selected === '') {
                          return (
                            <span style={{ color: 'grey', opacity: '0.6' }}>
                              {'Select the correct answer'}
                            </span>
                          )
                        }
                        return selected
                      }}
                      fullWidth>
                      {question.options.map((opt, optIndex) => (
                        <MenuItem key={optIndex} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}
            {(question.questionType === 'Yes/No' ||
              question.questionType === 'Yes/No-Lunch') && (
              <Box sx={{ marginTop: '15px' }}>
                <Typography sx={typography}>Correct Answer</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                  <FormControl>
                    <RadioGroup
                      row
                      value={question.correctAnswer}
                      onChange={e =>
                        handleYesNoAnswer(qIndex, e.target.value === 'true')
                      }>
                      <FormControlLabel
                        value='true'
                        control={<Radio />}
                        label='Yes'
                        sx={{ marginRight: 2 }}
                      />
                      <FormControlLabel
                        value='false'
                        control={<Radio />}
                        label='No'
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                {question.errorMessage && (
                  <Typography color='error' sx={{ mt: 1 }}>
                    {question.errorMessage}
                  </Typography>
                )}
              </Box>
            )}
            {question.questionType === 'shortAnswer' && (
              <Box sx={{ marginTop: '15px' }}>
                <Typography sx={typography}>Short Answer</Typography>
                <Box
                  sx={{
                    marginTop: '8px',
                  }}>
                  <FormControl
                    fullWidth
                    required
                    error={question.correctAnswer.trim() === ''}>
                    <Input
                      disableUnderline
                      placeholder='Enter the correct answer'
                      value={question.correctAnswer}
                      onChange={e =>
                        handleshortAnswerChange(qIndex, e.target.value)
                      }
                      sx={inputStyle}
                    />
                  </FormControl>
                </Box>
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={question.isRequired}
                  onChange={e =>
                    handlePropertyChange(qIndex, 'isRequired', e.target.checked)
                  }
                />
              }
              label='Required'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={question.isVisible}
                  onChange={e =>
                    handlePropertyChange(qIndex, 'isVisible', e.target.checked)
                  }
                />
              }
              label='Visible'
            />
          </Card>
        ))}
        <Button type='submit' variant='contained' color='primary' sx={btnStyle}>
          {mode === 'edit' ? 'Update' : 'Submit'}
        </Button>
      </form>
    </Box>
  )
}

export default AddQuestionForm
